import { type QueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';

import { client } from '@@api/client';
import { idUtilsWithError } from '@@components/Page/Content/useDetailPageResourcesLoader';
import { type ContentEntity } from '@@components/Page/types';
import { handleLoaderError } from '@@router/handleLoaderError';
import { selectContentLocale, useSettingsStore } from '@@scripts/store/settings/useSettingsStore';

/** The metadata of a detail page's content, which the loader and the page share */
export const metadataQuery = (metadataId: number) =>
    client.metadata.metadata.get.queryOptions({ params: { id: metadataId }, query: {} });

/**
 * Loads a detail page's content and its metadata before the page renders. A malformed or unknown
 * id shows the 404 page.
 */
export const loadContentDetail = async <Content extends { body: ContentEntity }, ContentError>(
    {
        queryClient,
        contentQuery,
        abortController,
    }: {
        queryClient: QueryClient;
        contentQuery: UseQueryOptions<Content, ContentError, Content, unknown[]>;
        /** The loader's, which the router aborts when another load replaces this one */
        abortController: AbortController;
    },
    id: string,
) => {
    if (idUtilsWithError(id).isError) {
        throw notFound();
    }

    // Cached data shows at once, and the page's queries refetch it in the background
    const content = await queryClient
        .query({ ...contentQuery, staleTime: 'static' })
        .catch(handleLoaderError);
    const metadata = await queryClient
        .query({ ...metadataQuery(content.body.metadataId), staleTime: 'static' })
        .catch(handleLoaderError);

    // Another item is opening, and the language is its loader's to set
    if (abortController.signal.aborted) {
        return;
    }

    // The queries the page builds when it renders send the metadata's language
    if (metadata.body.locale) {
        // The content went out with the language of the detail page opened before
        const requestedIn = selectContentLocale(useSettingsStore.getState());

        useSettingsStore.getState().setMetadataContentLocale(metadata.body.locale);

        // The content's key leaves the language out, so the page fetches it again in its own
        if (metadata.body.locale !== requestedIn) {
            void queryClient.invalidateQueries({ queryKey: contentQuery.queryKey });
        }
    }
};
